import { Badge, Menu, MenuItem, Stack, SxProps, TableCell, TableCellProps, Theme, Typography } from '@mui/material';
import color from '@theme/Colors';
import { omit } from 'lodash';
import { useState } from 'react';
import { BiDownArrowAlt } from 'react-icons/bi';
import { Sort } from '.';

export type BaseHeadProps<T> = {
    title: string | React.ReactNode;
    sx?: SxProps<Theme>;
    background?: string;
    align?: TableCellProps['align'];
    sortKey?: keyof T;
    sortedCount?: number;
    sort?: Sort;
    onChangeSort?: (sort: Sort, field: keyof T) => void;
    isLeftLabel?: boolean;
};

const TableHeadCellSort = <T,>(props: BaseHeadProps<T>) => {
    const { title, sx, align, background, sort, sortKey } = props;

    const [menuSortAnchor, setMenuSortAnchor] = useState<null | HTMLElement>(null);

    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        setMenuSortAnchor(event.currentTarget);
    };

    const handleCloseMenuSort = () => {
        setMenuSortAnchor(null);
    };

    const handleSort = (newSort: Sort) => {
        props.onChangeSort?.(newSort, sortKey as keyof T);
        handleCloseMenuSort();
    };

    return (
        <TableCell
            sx={{ background: background ?? '#FAFAFA', ...omit(sx, 'background') }}
            align={align ?? 'right'}
            onContextMenu={handleContextMenu}
        >
            <Stack
                direction={'row'}
                justifyContent={props.isLeftLabel ? 'flex-start' : 'center'}
                sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    mr: sort ? '-8px' : undefined,
                    '&:hover .sort-icon': {
                        opacity: 1,
                    },
                }}
                onClick={() => props.onChangeSort?.(sort === 'ASC' ? 'DSC' : 'ASC', sortKey as keyof T)}
            >
                <Badge
                    badgeContent={props.sortedCount ?? undefined}
                    sx={{
                        '& .MuiBadge-badge': {
                            border: `2px solid #FFFFFF`,
                            padding: '0 4px',
                            fontSize: 9,
                            top: 2,
                            backgroundColor: color.grey400,
                        },
                    }}
                >
                    {typeof title === 'string' ? (
                        <Typography fontSize={16} fontWeight={500}>
                            {title}
                        </Typography>
                    ) : (
                        title
                    )}
                </Badge>
                <Stack
                    className="sort-icon"
                    sx={{
                        display: 'grid',
                        placeItems: 'center',
                        ml: '2px',
                        opacity: sort ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out',
                    }}
                >
                    <BiDownArrowAlt
                        size={16}
                        style={{
                            color: color.grey600,
                            transition: 'all 0.3s',
                            transform: `rotate(${sort === 'ASC' ? 0 : -180}deg)`,
                        }}
                    />
                </Stack>
            </Stack>

            <Menu
                anchorEl={menuSortAnchor}
                open={Boolean(menuSortAnchor)}
                onClose={handleCloseMenuSort}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{ zIndex: 100, boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px' }}
            >
                <MenuItem onClick={() => handleSort('ASC')}>Sort Ascending</MenuItem>
                <MenuItem onClick={() => handleSort('DSC')}>Sort Descending</MenuItem>
                <MenuItem
                    onClick={() => {
                        props.onChangeSort?.(undefined as unknown as Sort, sortKey as keyof T);
                        handleCloseMenuSort();
                    }}
                >
                    Unsort
                </MenuItem>
            </Menu>
        </TableCell>
    );
};
export { TableHeadCellSort };
